// `…/candidate/profile/export` and `…/client-contact/client-contact/profile/export` — the list
// picker on the STANDALONE profile (SINGLE_PROFILE_EXPORT, cee70a51e8), in the profile's side
// view. The states render the lists' own `<extension-alert-candidates-export>` /
// `<extension-alert-client-contacts-export>` with the profile as the only checked entity and no
// hunter behind it.
//
// ⚠️ THE PANEL DOES NOT EXPORT, THE PROFILE DOES. The panel emits `OnCandidatesExport` /
// `OnClientContactsExport` and the PROFILE state's controller runs it (`runExport`), because the
// profile owns the outcome: on success it flags the record and returns to itself, on a rejected
// start it stays HERE so the user can retry. So this route hands its form up through the outlet
// context and draws nothing of the export itself — see `onExport` in the two profiles.
//
// This is a different route from `ExportPanelRoute` on purpose: that one belongs to the review,
// reads the hunter's alerts for the list name, and records the export in Woo; this one has no
// hunter, names the list after the profile, and records nothing.
import { useEffect, useState, type ReactElement } from 'react'
import { useLocation, useNavigate, useOutletContext } from 'react-router-dom'
import {
  fetchViewModel,
  getClientContactAutocomplete,
  getShortlistAutocomplete,
  type ExportListTarget,
} from '@woo-ext/api'
import { ExtensionViewWidth } from '@woo-ext/protocol'
import {
  CandidateExportPanel,
  getAtsLabel,
  profileDefaultListName,
  type ExportEntity,
  type ExportSubmitValues,
} from '@woo-ext/ui'

/** What the profile hands its side view. */
export interface ProfileExportContext {
  checked?: ReadonlyArray<{ id: number; name?: string | null }>
  /** `runExport({ kind, request })` — the profile starts the export and owns its outcome. */
  onExport?: (target: ExportListTarget) => void
}

/** The picker's form, as the request's list half — the panel decides which of the two it is. */
export function exportTargetFromForm(values: ExportSubmitValues): ExportListTarget {
  // `description: ''` is upstream's: the new-list branch always sends all three fields.
  return values.createNewList
    ? { name: values.name, description: '', isPublic: values.isPublic }
    : { listId: values.listId }
}

export interface ProfileExportRouteProps {
  entity?: ExportEntity
}

export function ProfileExportRoute({ entity = 'candidates' }: ProfileExportRouteProps = {}): ReactElement {
  const context = useOutletContext<ProfileExportContext | null>()
  const checked = context?.checked ?? []
  const navigate = useNavigate()
  const { pathname, search } = useLocation()
  const [atsName, setAtsName] = useState<string | undefined>(undefined)
  const [atsLabel, setAtsLabel] = useState<string | undefined>(undefined)

  useEffect(() => {
    let live = true
    fetchViewModel().then(
      (model) => {
        if (!live) return
        setAtsName(model.atsName ?? undefined)
        setAtsLabel(getAtsLabel(model))
      },
      () => undefined,
    )
    return () => {
      live = false
    }
  }, [])

  // `closeState: CandidateProfile` with the profile's id — `$state.go` inherits the rest.
  const close = (): void => {
    void navigate(`${pathname.replace(/\/export$/, '')}${search}`)
  }

  return (
    <CandidateExportPanel
      entity={entity}
      {...(atsName === undefined ? {} : { atsName })}
      {...(atsLabel === undefined ? {} : { atsLabel })}
      flexBasis={ExtensionViewWidth.Extended}
      fromHunter={false}
      alertName={profileDefaultListName(checked)}
      checked={checked}
      onSearchLists={entity === 'clientContacts' ? getClientContactAutocomplete : getShortlistAutocomplete}
      onSubmit={(values) => {
        // A deep link can open the panel before the profile has loaded; an export of nobody
        // creates an empty list in the customer's ATS.
        if (checked.length === 0) return
        context?.onExport?.(exportTargetFromForm(values))
      }}
      onClose={close}
    />
  )
}
